'use client';

import React, { useState } from 'react';
import { X, Clock, Layers, Target, Stethoscope, ChevronRight, ArrowLeft, Sparkles } from 'lucide-react';

interface ServiceTypeSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectModality: (modality: 'CLASICO' | 'PAUSA' | 'ZONAS' | 'CONSULTA', templateName?: string) => void;
}

export function ServiceTypeSelectModal({
  isOpen,
  onClose,
  onSelectModality
}: ServiceTypeSelectModalProps) {
  const [step, setStep] = useState<'TEMPLATES' | 'MODALITIES'>('TEMPLATES');

  if (!isOpen) return null;

  const handleClose = () => {
    setStep('TEMPLATES');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs transition-opacity animate-in fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div>
            {step === 'MODALITIES' && (
              <button
                type="button"
                onClick={() => setStep('TEMPLATES')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 mb-2 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Volver a plantillas</span>
              </button>
            )}
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
              {step === 'TEMPLATES' ? 'Agregar servicios' : 'Crear desde cero'}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {step === 'TEMPLATES'
                ? 'Elegí los que hacés y ajustá precio y duración.'
                : '¿Cómo querés que tus clientes lo reserven?'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-2 overflow-y-auto space-y-5 flex-1">
          {step === 'TEMPLATES' ? (
            <>
              {/* Peluquería */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                  Peluquería
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    onClick={() => onSelectModality('CLASICO', 'Peluquería unisex')}
                    className="p-3 bg-slate-50/70 hover:bg-emerald-50/60 border border-slate-200/80 hover:border-emerald-200 rounded-2xl cursor-pointer transition-all flex items-center gap-3 group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=100&h=100&fit=crop"
                      alt="Peluquería unisex"
                      className="w-11 h-11 rounded-xl object-cover"
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-800 group-hover:text-emerald-800">
                        Peluquería unisex
                      </p>
                      <p className="text-[11px] text-slate-400">36 servicios</p>
                    </div>
                  </div>

                  <div
                    onClick={() => onSelectModality('CLASICO', 'Peluquería de mujer')}
                    className="p-3 bg-slate-50/70 hover:bg-emerald-50/60 border border-slate-200/80 hover:border-emerald-200 rounded-2xl cursor-pointer transition-all flex items-center gap-3 group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=100&h=100&fit=crop"
                      alt="Peluquería de mujer"
                      className="w-11 h-11 rounded-xl object-cover"
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-800 group-hover:text-emerald-800">
                        Peluquería de mujer
                      </p>
                      <p className="text-[11px] text-slate-400">34 servicios</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Barbería */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                  Barbería
                </p>
                <div
                  onClick={() => onSelectModality('CLASICO', 'Barbería')}
                  className="p-3 bg-slate-50/70 hover:bg-emerald-50/60 border border-slate-200/80 hover:border-emerald-200 rounded-2xl cursor-pointer transition-all flex items-center gap-3 group"
                >
                  <img
                    src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=100&h=100&fit=crop"
                    alt="Barbería"
                    className="w-11 h-11 rounded-xl object-cover"
                  />
                  <div>
                    <p className="text-xs font-bold text-slate-800 group-hover:text-emerald-800">
                      Barbería
                    </p>
                    <p className="text-[11px] text-slate-400">15 servicios</p>
                  </div>
                </div>
              </div>

              {/* Centros de Estética */}
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                  Centros de estética
                </p>
                <div
                  onClick={() => onSelectModality('CLASICO', 'Centro de estética')}
                  className="p-3 bg-slate-50/70 hover:bg-emerald-50/60 border border-slate-200/80 hover:border-emerald-200 rounded-2xl cursor-pointer transition-all flex items-center gap-3 group"
                >
                  <img
                    src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=100&h=100&fit=crop"
                    alt="Centro de estética"
                    className="w-11 h-11 rounded-xl object-cover"
                  />
                  <div>
                    <p className="text-xs font-bold text-slate-800 group-hover:text-emerald-800">
                      Centro de estética
                    </p>
                    <p className="text-[11px] text-slate-400">79 servicios</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* MODALITIES */
            <div className="space-y-3">
              {/* Turno clásico */}
              <div
                onClick={() => onSelectModality('CLASICO')}
                className="p-4 bg-white hover:bg-emerald-50/40 border border-slate-200/90 hover:border-emerald-300 rounded-2xl cursor-pointer transition-all flex items-start gap-4 group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-900">
                    Turno clásico
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Un horario, un precio. Es el de siempre.
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 self-center" />
              </div>

              {/* Turno con pausa */}
              <div
                onClick={() => onSelectModality('PAUSA')}
                className="p-4 bg-white hover:bg-emerald-50/40 border border-slate-200/90 hover:border-emerald-300 rounded-2xl cursor-pointer transition-all flex items-start gap-4 group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-900">
                    Turno con pausa
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Se libera en el medio y podés atender a otro.
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 self-center" />
              </div>

              {/* Servicio por zonas */}
              <div
                onClick={() => onSelectModality('ZONAS')}
                className="p-4 bg-white hover:bg-emerald-50/40 border border-slate-200/90 hover:border-emerald-300 rounded-2xl cursor-pointer transition-all flex items-start gap-4 group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Target className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-900">
                    Servicio por zonas
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    El precio sale de cuántas zonas elige el cliente.
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 self-center" />
              </div>

              {/* Consulta + presupuesto */}
              <div
                onClick={() => onSelectModality('CONSULTA')}
                className="p-4 bg-white hover:bg-emerald-50/40 border border-slate-200/90 hover:border-emerald-300 rounded-2xl cursor-pointer transition-all flex items-start gap-4 group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-900">
                    Consulta + presupuesto
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Primero lo ves, después le pasás el precio.
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 self-center" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-3 border-t border-slate-100">
          {step === 'TEMPLATES' ? (
            <button
              type="button"
              onClick={() => setStep('MODALITIES')}
              className="w-full py-3 px-4 bg-white hover:bg-emerald-50 border border-slate-200/90 hover:border-emerald-400 text-emerald-700 font-bold text-xs rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xs"
            >
              <span>+ Crear desde cero</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-3 px-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-2xl transition-all"
            >
              Volver al catálogo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
